import debounce from 'lodash.debounce';
import * as bootstrap from 'bootstrap';
import '../../shared/modules/dotlottie';
import jsKit from '../../shared/modules/jsKit';
import { swipers } from '../../shared/modules/swipers';
import sp from '../../shared/modules/sp';
import topLoader from '../../shared/modules/topLoader';
import { addBackToTop } from 'vanilla-back-to-top';
import { RippleEffect } from '../../shared/modules/ripple';
import navScroll from '../../shared/modules/navScroll';
import cookiesSettings from '../../shared/modules/cookiesSettings';
import { initInvoiceSummary, registerClientEvents } from '../../shared/modules/client';
import { initBasketDiscountCoupon, registerBasketEvents } from '../../shared/modules/basket';
import { registerOrderDetailsEvents } from '../../shared/modules/orderDetails';
import { productBasketEvents } from './modules/productBasket';
import { basketPluginEvents } from '../../shared/modules/basketPlugins';
import { initPopupBanners } from '../../shared/modules/popupBanners';
import { headerSpecialFunctionsEvents } from '../../shared/modules/headerSpecialFunctions';
import { deliveryPricelistEvents } from '../../shared/modules/deliveryPricelist';
import { initFlashMessages } from '../../shared/modules/flashMessages';
import { initOrderStatus } from '../../shared/modules/orderStatus';
import { ceneoOrderEvents, initCeneoOrder } from '../../shared/modules/ceneoOrder';
import { infoBarEvents } from '../../shared/modules/infoBar';
import { headerAlertEvents, initHeaderAlert } from '../../shared/modules/headerAlert';
import slideshow from '../../shared/modules/slideshow';
import { initOAuth } from '../../shared/modules/oAuth';
import { initGlobalBootstrapComponents } from '../../shared/modules/bootstrapComponents';
import { initModalAutofocus, initPopupContainerCleanup } from '../../shared/modules/modalLifecycle';

const spEvents = [
  ['ga4EventClick', (e) => sp.GA4Event(e)],
  ['ga4EventChange', (e) => sp.GA4Event(e), 'change'],

  ['jsShowSearchHintsInput', sp.searchOnEnter, 'keyup'],
  ['jsShowSearchHintsInput', debounce((e) => sp.showSearchHints(e), 1000), 'input'],
  ['jsSearchProducts', (e) => sp.searchProducts(e)],
  ['jsClearSearchProducts', (e) => sp.clearSearchProducts(e)],
  ['jsClearSearchProductsChange', (e) => sp.clearSearchProducts(e), 'change'],

  ['jsCalculateFinalDeliveryTimeInput', (e) => sp.calculateFinalDeliveryTime(e)],
  ['jsCalculateFinalDeliveryTimeChange', (e) => sp.calculateFinalDeliveryTime(e)],
  ['jsUpdatePersonalizationImageInput', debounce((e) => sp.updatePersonalizationImage(e), 800), 'input'],
  ['jsUpdatePersonalizationImageChange', (e) => sp.updatePersonalizationImage(e), 'change'],

  ['jsShowStockAmountChange', (e) => sp.showStockAmount(e), 'change'],
  ['jsSanitizeIntegerBlur', (e) => sp.sanitizeIntegerBlur(e), 'blur'],

  ['jsWholesaleAmountChangeInput', (e) => sp.wholesaleAmountChange(e), 'input'],

  ['jsLoadBestProducts', (e) => sp.loadBestProducts(e)],
  ['jsLoadLatestProducts', (e) => sp.loadLatestProducts(e)],
  ['jsLoadTopSaleProducts', (e) => sp.loadTopSaleProducts(e)],

  ['jsClearAmountChange', (e) => sp.clearAmountInputParam(e), 'change'],
  ['jsSetAmountCheckboxStateInput', (e) => sp.setAmountCheckboxState(e), 'input'],
  ['jsSetAmountInput', debounce(sp.setAmount, 800), 'input'],

  ['jsClearPriceChange', (e) => sp.clearPriceInputParam(e), 'change'],
  ['jsSetPriceCheckboxStateInput', (e) => sp.setPriceCheckboxState(e), 'input'],
  ['jsSetPriceInput', debounce(sp.setPrice, 800), 'input'],

  ['jsClearDeliveryTimeChange', (e) => sp.clearDeliveryTimeInputParam(e), 'change'],
  ['jsSetDeliveryCheckboxStateChange', (e) => sp.setDeliveryTimeCheckboxState(e), 'change'],
  ['jsSetDeliveryChange', debounce(sp.setPrice, 800), 'change'],

  ['jsLoadProductListItems', sp.loadProductListItems],
  ['jsLoadProductListClick', sp.loadProductList],
  ['jsLoadProductListChange', sp.loadProductList, 'change'],

  ['jsToggleSideMenu', (e) => sp.toggleSideMenuClick(e)],
  ['jsScrollToId', (e) => {
    e.preventDefault();
    jsKit.scrollToID(e.delegateTarget.dataset.jsScrollToId);
  }],
  ['jsPriceToggle', (e) => sp.priceToggle(e), 'change'],
  ['jsChangeCurrency', (e) => sp.changeCurrency(e), 'change'],
  ['jsSetLanguage', (e) => sp.setLanguage(e), 'change'],
  ['jsSendProductQuestion', (e) => sp.sendProductQuestion(e)],
  ['jsSubscribeNewsletter', (e) => sp.subscribeNewsletter(e)],
  ['jsSaveUnavailableProductForm', (e) => sp.saveUnavailableProductForm(e)],
  ...productBasketEvents,
  ...basketPluginEvents,
  ...headerSpecialFunctionsEvents,
  ...deliveryPricelistEvents,
  ...infoBarEvents,
  ...headerAlertEvents,
  ...ceneoOrderEvents,
];

const registerSpEvents = () => {
  spEvents.forEach(([dataItem, handler, eventName]) => {
    jsKit.attachEventByDataSet(dataItem, handler, eventName);
  });
};

initOAuth();

let filterSearchReady = null;

const loadFilterSearch = () => {
  if (!filterSearchReady) {
    filterSearchReady = import(/* webpackChunkName: "filter-search" */ '../../shared/modules/filterSearch');
  }

  return filterSearchReady;
};

const initDateRangePickers = () => {
  if (!document.querySelector('[data-js-date-range-picker]')) {
    return;
  }

  import(/* webpackChunkName: "date-range-picker" */ '../../shared/modules/dateRangePicker')
    .then(({ initDateRangePickers: init }) => init());
};

const registerFilterSearchEvents = () => {
  jsKit.attachEventByDataSet('jsFilterSearch', (e) => {
    const target = e.delegateTarget || e.target;
    loadFilterSearch().then(({ setupFilterSearch }) => {
      if (target.isConnected) {
        setupFilterSearch({ target });
      }
    });
  }, 'input');

  jsKit.attachEventByDataSet('jsFilterReset', (e) => {
    const target = e.delegateTarget || e.target;
    loadFilterSearch().then(({ resetFilterSearch }) => {
      if (target.isConnected) {
        resetFilterSearch({ target });
      }
    });
  }, 'click');

  // Załaduj chunk z wyprzedzeniem, jeśli wyszukiwarka jest już w pierwszym widoku.
  if (document.querySelector('[data-js-filter-search]')) {
    loadFilterSearch();
  }
};

document.addEventListener('DOMContentLoaded', () => {
  jsKit.installLegacyNotificationApi();
  addBackToTop();
  topLoader();
  cookiesSettings(bootstrap);

  if (document.querySelector('[data-js-dashboard-calendar]')) {
    import(/* webpackChunkName: "dashboard-calendar" */ './modules/dashboard-calendar').then(({ setupDashboardCalendar }) => {
      setupDashboardCalendar(document.querySelector('[data-js-dashboard-calendar]'));
    });
  }

  if (document.querySelectorAll('.nav-scroll')) {
    navScroll(document.querySelectorAll('.nav-scroll'));
  }

  swipers.swiperProductCardInit();
  swipers.swiperLGInit();
  swipers.swiperJumbotronInit();
  swipers.swiperRCInit();
  swipers.swiperBlogInit();
  swipers.topSaleInit();
  swipers.swiperTrustedUsInit();
  swipers.swiperProducerInit();
  swipers.swiperAdvantagesInit();
  swipers.swiperEnthusiastsInit();
  swipers.swiperOpinionsInit();
  swipers.swiperRelatedProductsInit();
  swipers.swiperWrappedInit();

  sp.GA4Event();
  sp.calculateFinalDeliveryTime();
  sp.appModal();
  sp.navBarDropdown();
  sp.tabsHash();

  sp.setAmountCheckboxState();
  sp.setPriceCheckboxState();

  jsKit.initPopState();
  sp.initSegments();
  sp.imageZoomInit();

  registerSpEvents();
  registerFilterSearchEvents();
  registerClientEvents();
  registerBasketEvents();
  registerOrderDetailsEvents();
  initInvoiceSummary();
  initBasketDiscountCoupon();
  initDateRangePickers();
  initPopupBanners();
  initFlashMessages();
  initHeaderAlert();
  initOrderStatus();
  initCeneoOrder();
  initGlobalBootstrapComponents();
  initPopupContainerCleanup();
  initModalAutofocus();
  document.querySelectorAll('[data-js-slideshow-autostart]').forEach((element) => {
    slideshow.showSlideShow(element.dataset.jsSlideshowAutostart);
  });
}, false);
